import React, { useEffect, useState } from 'react';
import { Loader2, History, TrendingDown, TrendingUp, Search } from 'lucide-react';
import { Input } from './ui/input';
import { historyAPI } from '../services/api';

const FIELD_LABELS = { price: 'Prix de vente TTC', stock: 'Stock entrepôt', purchase_price: "Prix d'achat HT", supplier_stock: 'Stock fournisseur' };
const FIELD_STYLE = { price: 'bg-orange-100 text-orange-700', stock: 'bg-gray-100 text-gray-700', purchase_price: 'bg-blue-100 text-blue-700', supplier_stock: 'bg-indigo-100 text-indigo-700' };
const fmt = (field, v) => (v == null ? '—' : field.includes('stock') ? String(v) : `${Number(v).toFixed(2)} €`);

// Admin: chronological journal of price / stock changes (per product or global)
const PriceHistoryList = ({ productId = null, limit = 50, refreshKey = 0, showProduct = true, compact = false }) => {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState('');
  const [field, setField] = useState('');

  useEffect(() => {
    setRows(null);
    historyAPI.list(productId, limit, field || undefined).then(setRows).catch(() => setRows([]));
  }, [productId, limit, field, refreshKey]);

  const visible = (rows || []).filter(r => !filter || [r.product_name, r.brand, r.ref, r.author, r.source, r.supplier_name].some(v => v && v.toLowerCase().includes(filter.toLowerCase())));

  return (
    <div data-testid={productId ? 'product-price-history' : 'price-history'}>
      {!compact && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {showProduct && (
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input data-testid="price-history-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer par produit, auteur, source…" className="pl-8 h-9 w-72" />
            </div>
          )}
          <select data-testid="price-history-field" value={field} onChange={(e) => setField(e.target.value)} className="h-9 border border-gray-300 rounded-md px-2 text-sm bg-white">
            <option value="">Tous les champs</option>
            {Object.entries(FIELD_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <span className="text-xs text-gray-500 ml-auto">{rows ? `${visible.length} changement${visible.length > 1 ? 's' : ''}` : ''}</span>
        </div>
      )}
      {rows === null ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-orange-500" size={18} /></div>
      ) : visible.length === 0 ? (
        <p className="text-sm text-gray-500 flex items-center gap-2 py-3" data-testid="price-history-empty"><History size={14} /> Aucun changement enregistré{productId ? ' pour ce produit' : ''}.</p>
      ) : (
        <div className="border rounded-lg overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="price-history-table">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Date</th>
                {showProduct && <th className="px-3 py-2 text-left">Produit</th>}
                <th className="px-3 py-2 text-left">Champ</th>
                <th className="px-3 py-2 text-right">Avant</th>
                <th className="px-3 py-2 text-right">Après</th>
                <th className="px-3 py-2 text-right">Variation</th>
                <th className="px-3 py-2 text-left">Par / source</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {visible.map(r => (
                <tr key={r.id} data-testid={`price-history-row-${r.id}`}>
                  <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{new Date(r.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  {showProduct && <td className="px-3 py-2"><p className="font-medium text-gray-900">{r.product_name}</p><p className="text-xs text-gray-400">{r.brand} · Réf. {r.ref}</p></td>}
                  <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FIELD_STYLE[r.field] || 'bg-gray-100'}`}>{FIELD_LABELS[r.field] || r.field}</span>{r.supplier_name && <span className="block text-[11px] text-gray-500 mt-0.5">{r.supplier_name}</span>}</td>
                  <td className="px-3 py-2 text-right text-gray-500">{fmt(r.field, r.old)}</td>
                  <td className="px-3 py-2 text-right font-semibold text-gray-900">{fmt(r.field, r.new)}</td>
                  <td className="px-3 py-2 text-right">
                    {r.change_pct == null ? <span className="text-gray-400">—</span> : (
                      <span className={`inline-flex items-center gap-1 font-medium ${r.change_pct > 0 ? 'text-green-700' : 'text-red-600'}`}>{r.change_pct > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}{r.change_pct > 0 ? '+' : ''}{r.change_pct} %</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-600"><p>{r.author}</p><p className="text-gray-400">{r.source}</p></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PriceHistoryList;
